import { useMemo } from 'react';
import CalendarIcon from 'components/icons/calendar2';
import InboundCallIcon from 'components/icons/inboundCall';
import OutboundCallIcon from 'components/icons/outboundCall';
import ClockIcon from 'components/icons/clock2';
import DoubleCheckIcon from 'components/icons/doubleCheck';
import SummaryItem from 'components/SummaryItem';
import ChartContainer from 'components/InsightSectionContainer';
import { useParams } from 'react-router-dom';
import Footer from 'components/Footer';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveHeatMap } from '@nivo/heatmap';
import {
    Container, SumaryContainer, ChartWrapper,
} from './styled';
import useCalls from './hooks/useCalls';
import getSummary from './helpers/getSummary';
import formatDuration from './helpers/formatDuration';
import formatPeakHour from './helpers/formatPeakHour';
import { CallLineTooltip, MinuteLineTooltip, HeatmapTooltip } from './Tooltips';
import CustomPointSymbol from './CustomPointSymbol';
import { Hour } from './types';

const Insights = () => {
    const { agentId } = useParams();
    const { calls, isLoading } = useCalls(agentId as string);
    const {
        totalCalls,
        averageInboundCallDuration,
        averageOutboundCallDuration,
        averageInboundCallsPerDay,
        averageOutboundCallsPerDay,
        inboundPeakHour,
        outboundPeakHour,
        inboundCallsByDate,
        outboundCallsByDate,
        totalInboundCalls,
        totalOutboundCalls,
        inboundDurationByDate,
        outboundDurationByDate,
        inboundCallsByHour,
        outboundCallsByHour,
        totalInboundSeconds,
        totalOutboundSeconds,
    } = getSummary(calls);

    const callsLineData = useMemo(() => [
        {
            id: 'Inbound',
            color: '#53D28C',
            data: Object.entries(inboundCallsByDate).map(([date, value]) => ({
                x: date,
                y: value,
            })),
        },
        {
            id: 'Outbound',
            color: '#6258CC',
            data: Object.entries(outboundCallsByDate).map(([date, value]) => ({
                x: date,
                y: value,
            })),
        },
    ], [inboundCallsByDate, outboundCallsByDate]);

    const minutesLineData = useMemo(() => [
        {
            id: 'Inbound',
            color: '#53D28C',
            data: Object.entries(inboundDurationByDate).map(([date, value]) => ({
                x: date,
                y: value,
            })),
        },
        {
            id: 'Outbound',
            color: '#6258CC',
            data: Object.entries(outboundDurationByDate).map(([date, value]) => ({
                x: date,
                y: value,
            })),
        },
    ], [inboundDurationByDate, outboundDurationByDate]);

    const heatmapData = useMemo(() => {
        const hours = Array.from({ length: 24 }, (_, i) => i as Hour);
        return [
            {
                id: 'Inbound',
                data: hours.map((hour) => ({
                    x: `${String(hour).padStart(2, '0')}:00`,
                    y: inboundCallsByHour[hour],
                })),
            },
            {
                id: 'Outbound',
                data: hours.map((hour) => ({
                    x: `${String(hour).padStart(2, '0')}:00`,
                    y: outboundCallsByHour[hour],
                })),
            },
        ];
    }, [inboundCallsByHour, outboundCallsByHour]);

    return (
        <Container>
            <SumaryContainer>
                <SummaryItem
                    value={isLoading ? 'No Data' : String(totalInboundCalls)}
                    label="Total Inbound Calls"
                    icon={<InboundCallIcon />}
                    isLoading={isLoading}
                />
                <SummaryItem
                    value={isLoading ? 'No Data' : `${averageInboundCallsPerDay.toFixed(2)}`}
                    label="Avg. Calls Per Day"
                    icon={<CalendarIcon />}
                    isLoading={isLoading}
                />
                <SummaryItem
                    value={isLoading ? 'No Data' : formatDuration(averageInboundCallDuration)}
                    label="Avg. Call Duration"
                    icon={<ClockIcon />}
                    isLoading={isLoading}
                />
                <SummaryItem
                    value={isLoading ? 'No Data' : formatPeakHour(inboundPeakHour)}
                    label="Peak Time"
                    icon={<DoubleCheckIcon />}
                    isLoading={isLoading}
                />
            </SumaryContainer>
            <SumaryContainer>
                <SummaryItem
                    value={isLoading ? 'No Data' : String(totalOutboundCalls)}
                    label="Total Outbound Calls"
                    icon={<OutboundCallIcon />}
                    isLoading={isLoading}
                />
                <SummaryItem
                    value={isLoading ? 'No Data' : `${averageOutboundCallsPerDay.toFixed(2)}`}
                    label="Avg. Calls Per Day"
                    icon={<CalendarIcon />}
                    isLoading={isLoading}
                />
                <SummaryItem
                    value={isLoading ? 'No Data' : formatDuration(averageOutboundCallDuration)}
                    label="Avg. Call Duration"
                    icon={<ClockIcon />}
                    isLoading={isLoading}
                />
                <SummaryItem
                    value={isLoading ? 'No Data' : formatPeakHour(outboundPeakHour)}
                    label="Peak Time"
                    icon={<DoubleCheckIcon />}
                    isLoading={isLoading}
                />
            </SumaryContainer>
            <ChartContainer
                isLoading={isLoading}
                label="Inbound vs Outbound"
                description={`Total of calls ${totalCalls} (Inbound: ${totalInboundCalls}, Outbound: ${totalOutboundCalls})`}
            >
                <ChartWrapper>
                    <ResponsiveLine
                        data={callsLineData}
                        margin={{
                            top: 20, right: 10, bottom: 60, left: 30,
                        }}
                        xScale={{ type: 'point' }}
                        yScale={{
                            type: 'linear',
                            min: 'auto',
                            max: 'auto',
                        }}
                        colors={{ datum: 'color' }}
                        pointSize={16}
                        pointBorderWidth={1}
                        pointBorderColor={{
                            from: 'color',
                            modifiers: [['darker', 0.3]],
                        }}
                        pointSymbol={CustomPointSymbol}
                        useMesh
                        enablePointLabel
                        pointLabel={({ data: d }) => (d.y === 0 ? '' : String(d.y))}
                        legends={[
                            {
                                anchor: 'top',
                                direction: 'row',
                                justify: false,
                                translateX: 0,
                                translateY: -20,
                                itemsSpacing: 0,
                                itemDirection: 'left-to-right',
                                itemWidth: 80,
                                itemHeight: 20,
                                itemOpacity: 0.75,
                                symbolSize: 12,
                                symbolShape: 'circle',
                                symbolBorderColor: 'rgba(0, 0, 0, .5)',
                                effects: [
                                    {
                                        on: 'hover',
                                        style: {
                                            itemBackground: 'rgba(0, 0, 0, .03)',
                                            itemOpacity: 1,
                                        },
                                    },
                                ],
                            },
                        ]}
                        axisBottom={{
                            tickRotation: -45,
                            tickPadding: 10,
                        }}
                        tooltip={CallLineTooltip}
                    />
                </ChartWrapper>
            </ChartContainer>
            <ChartContainer
                isLoading={isLoading}
                label="Talk Time (Inbound & Outbound)"
                description={`Total of minutes: ${formatDuration(totalInboundSeconds + totalOutboundSeconds)} (${formatDuration(totalInboundSeconds)} inbound, ${formatDuration(totalOutboundSeconds)} outbound)`}
            >
                <ChartWrapper>
                    <ResponsiveLine
                        data={minutesLineData}
                        margin={{
                            top: 20, right: 10, bottom: 60, left: 30,
                        }}
                        xScale={{ type: 'point' }}
                        yScale={{
                            type: 'linear',
                            min: 'auto',
                            max: 'auto',
                        }}
                        colors={{ datum: 'color' }}
                        pointSize={16}
                        pointBorderWidth={1}
                        pointBorderColor={{
                            from: 'color',
                            modifiers: [['darker', 0.3]],
                        }}
                        pointSymbol={CustomPointSymbol}
                        useMesh
                        legends={[
                            {
                                anchor: 'top',
                                direction: 'row',
                                justify: false,
                                translateX: 0,
                                translateY: -20,
                                itemsSpacing: 0,
                                itemDirection: 'left-to-right',
                                itemWidth: 80,
                                itemHeight: 20,
                                itemOpacity: 0.75,
                                symbolSize: 12,
                                symbolShape: 'circle',
                                symbolBorderColor: 'rgba(0, 0, 0, .5)',
                                effects: [
                                    {
                                        on: 'hover',
                                        style: {
                                            itemBackground: 'rgba(0, 0, 0, .03)',
                                            itemOpacity: 1,
                                        },
                                    },
                                ],
                            },
                        ]}
                        axisBottom={{
                            tickRotation: -45,
                            tickPadding: 10,
                        }}
                        tooltip={MinuteLineTooltip}
                    />
                </ChartWrapper>
            </ChartContainer>
            <ChartContainer
                isLoading={isLoading}
                label="Call Volume by Hour"
                description={`See when calls are most frequent throughout the day (${totalInboundCalls} inbound, ${totalOutboundCalls} outbound)`}
            >
                <ChartWrapper>
                    <ResponsiveHeatMap
                        data={heatmapData}
                        margin={{
                            top: 60, right: 40, bottom: 60, left: 80,
                        }}
                        valueFormat=">-.0f"
                        inactiveOpacity={1}
                        axisTop={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: -45,
                            legend: 'Hour of day',
                            legendOffset: -46,
                        }}
                        axisLeft={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                        }}
                        colors={(cell) => {
                            // Base colors
                            const inboundBase = '#53D28C'; // Green
                            const outboundBase = '#6258CC'; // Purple

                            // Choose base color based on call type
                            const baseColor = cell.serieId === 'Inbound' ? inboundBase : outboundBase;

                            // Calculate opacity based on value (higher value = more intense color)
                            // Find max value in the data to normalize
                            const allValues = heatmapData
                                .flatMap((serie) => serie.data.map((d) => d.y));
                            const maxValue = Math.max(...allValues, 1); // Prevent division by zero

                            // Calculate opacity between 0.1 (minimum) and 1.0 (maximum)
                            const value = cell.value ?? 0;
                            // Use at least 0.1 opacity even for zero values so they're visible
                            const opacity = value === 0 ? 0.1 : (0.3 + (0.7 * (value / maxValue)));

                            // Return the color with appropriate opacity
                            return `${baseColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;
                        }}
                        enableLabels
                        labelTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                        borderColor={{ from: 'color', modifiers: [['darker', 0.4]] }}
                        legends={[
                            {
                                anchor: 'bottom',
                                translateX: 0,
                                translateY: 30,
                                length: 400,
                                thickness: 8,
                                direction: 'row',
                                tickPosition: 'after',
                                tickSize: 3,
                                tickSpacing: 4,
                                tickOverlap: false,
                                title: 'Call volume',
                                titleAlign: 'start',
                                titleOffset: 4,
                            },
                        ]}
                        tooltip={({ cell }) => HeatmapTooltip({
                            cell: {
                                serieId: cell.serieId,
                                value: cell.data.y,
                            },
                        })}
                    />
                </ChartWrapper>
            </ChartContainer>
            <Footer />
        </Container>
    );
};

export default Insights;
